/**
 * SOLID Review
 * S — Strong. One concern: the table's handler for each kind of server event, built once.
 * O — Strong. A new event is one more handler here, and tsc names a missing one.
 * L — Strong. Implements the table's part of the event map exactly.
 * I — Strong. One field per kind of table event.
 * D — Strong. The decisions port, the table's step and the resync, injected.
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { TTableEvent } from '../ports/ClientServerEvents.contract';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { IServiceSerialStep } from '../shared/ClientSteps';
import type { TServerEventHandlers, TServerEventPayload } from '../shared/ServerEvents';
import { DecisionStep } from '../shared/DecisionStep';
import { ConnectivityUseCase, type IConfigDepConnectivity } from './ConnectivityUseCase';

/** The table's kinds of delivery, each with its handler. */
type TTableEventHandlers = Pick<TServerEventHandlers, TTableEvent['type']>;
type THandler<K extends TTableEvent['type']> = IUseCase<TServerEventPayload<K>, TPortOutcome>;

export interface IConfigDepTableEvents {
  readonly decisions: IPortClientDecisions;
  readonly step: IServiceSerialStep<IPropsClientTableInput>;
  readonly resync: IUseCase<ICommandNothing, TPortOutcome>;
}

/**
 * THE TABLE'S EVENTS (frontend-application-oo P3, 2026-10-28) — what replaced `TableContext.deliver`'s switch: each event its
 * own handler, one step of the table with the event's decision (`DecisionStep`), and the socket's state its own use case
 * (`ConnectivityUseCase`, which starts a resync when one is owed).
 */
export class TableEventHandlers implements TTableEventHandlers {
  readonly roundStarted: THandler<'roundStarted'>;
  readonly claimAnnounced: THandler<'claimAnnounced'>;
  readonly cupLifted: THandler<'cupLifted'>;
  readonly diceRolled: THandler<'diceRolled'>;
  readonly roomUpdated: THandler<'roomUpdated'>;
  readonly playerSuspended: THandler<'playerSuspended'>;
  readonly playerResumed: THandler<'playerResumed'>;
  readonly roomAbandoned: THandler<'roomAbandoned'>;
  readonly decisionCountdown: THandler<'decisionCountdown'>;
  readonly tableView: THandler<'tableView'>;
  readonly connectivity: THandler<'connectivity'>;

  constructor(deps: IConfigDepTableEvents) {
    const { decisions: d, step } = deps;
    this.roundStarted = new DecisionStep(step, (ctx, input, payload: TServerEventPayload<'roundStarted'>) => d.roundStarted(ctx, input, payload));
    this.claimAnnounced = new DecisionStep(step, (ctx, input, payload: TServerEventPayload<'claimAnnounced'>) => d.claimAnnounced(ctx, input, payload));
    this.cupLifted = new DecisionStep(step, (ctx, input, payload: TServerEventPayload<'cupLifted'>) => d.cupLifted(ctx, input, payload));
    this.diceRolled = new DecisionStep(step, (ctx, input, payload: TServerEventPayload<'diceRolled'>) => d.diceRolled(ctx, input, payload));
    this.roomUpdated = new DecisionStep(step, (ctx, input, payload: TServerEventPayload<'roomUpdated'>) => d.roomUpdated(ctx, input, payload));
    this.playerSuspended = new DecisionStep(step, (ctx, input, payload: TServerEventPayload<'playerSuspended'>) => d.playerSuspended(ctx, input, payload));
    this.playerResumed = new DecisionStep(step, (ctx, input, payload: TServerEventPayload<'playerResumed'>) => d.playerResumed(ctx, input, payload));
    this.roomAbandoned = new DecisionStep(step, (ctx, input, payload: TServerEventPayload<'roomAbandoned'>) => d.roomAbandoned(ctx, input, payload));
    this.decisionCountdown = new DecisionStep(step, (ctx, input, payload: TServerEventPayload<'decisionCountdown'>) => d.decisionCountdown(ctx, input, payload));
    this.tableView = new DecisionStep(step, (ctx, input, payload: TServerEventPayload<'tableView'>) => d.tableView(ctx, input, payload));
    const connectivity: IConfigDepConnectivity = deps;
    this.connectivity = new ConnectivityUseCase(connectivity);
  }
}
